import Certificate from '../dto/Certificate';
import Error from '../model/Error';
import Helper from '../accounting/Helper.js';
import axios from 'axios';

export default class CertificateRepository {

    static pdfUrl(certificate, disposition) {
        const type = certificate.type === 'tax' ? 'tax' : 'cost';

        return `/certificates/${type}/${certificate.id}/${disposition}`;
    }

    /**
     * Fetched over XHR rather than followed as a link so a missing PDF can be reported in place. A plain href
     * navigates the tab to the API's JSON error body and the user loses the list she was working in.
     */
    static fetchPdf(certificate, disposition) {
        return new Promise((resolve, reject) => {
            axios
                .get(CertificateRepository.pdfUrl(certificate, disposition), { responseType: 'blob' })
                .then(response => resolve(response))
                .catch(error => {
                    if (!error.response) {
                        reject(error);

                        return;
                    }

                    // The error body arrives as a Blob because of responseType, so it needs reading before parsing.
                    // A non-JSON body - a proxy's HTML error page, say - makes that parse throw, so fall back to
                    // the axios error rather than replacing it with a SyntaxError, as OfferingRepository does. The
                    // status survives either way: callers read it off error.status or error.response.status.
                    Error.fromBlob(error.response).then(reject, () => reject(error));
                });
        });
    }

    static downloadPdf(certificate) {
        return CertificateRepository.fetchPdf(certificate, 'attachment').then(response => {
            const objectUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = objectUrl;
            link.setAttribute('download', CertificateRepository.downloadName(response, certificate));
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
        });
    }

    /**
     * getFilenameFromHeaders() indexes into content-disposition unguarded, so a header that is missing or not
     * quoted would throw after a download that actually succeeded. The row already carries the name.
     */
    static downloadName(response, certificate) {
        const disposition = response.headers?.['content-disposition'];
        if (typeof disposition !== 'string' || !disposition.includes('"')) {
            return certificate.filename;
        }

        return new Helper().getFilenameFromHeaders(response.headers) || certificate.filename;
    }

    static findByChildAndInvoiceRecipient(childId, invoiceRecipientIdentifier) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/children/${childId}/invoice-recipients/${invoiceRecipientIdentifier}/certificates`, {  })
                .then(response => resolve(response.data.data.map(data => Certificate.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createTaxCertificate(childId, invoiceRecipient, costTypes, year, paidOnly, separateFoodAndOtherCosts) {
        return new Promise((resolve, reject) => {
            axios
                .post(`/children/${childId}/invoice-recipients/${invoiceRecipient.identifier}/certificates/tax`, {
                    costTypes: costTypes,
                    year: year,
                    paidOnly: paidOnly,
                    separateFoodAndOtherCosts: separateFoodAndOtherCosts
                })
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createCostCertificate(childId, invoiceRecipient, createDetails, costTypes, start, end, paidOnly) {
        return new Promise((resolve, reject) => {
            axios
                .post(`/children/${childId}/invoice-recipients/${invoiceRecipient.identifier}/certificates/cost`, {
                    createDetails: createDetails,
                    childId: childId,
                    costTypes: costTypes,
                    start: start,
                    end: end,
                    paidOnly: paidOnly
                })
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static remove(certificate)
    {
        const url = certificate.type === 'tax' ? `/certificates/tax/${certificate.id}` : `/certificates/cost/${certificate.id}`

        return new Promise((resolve, reject) => {
            axios
                .delete(url)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }
}
